<template>
  <Content
    class="ai-order-entry-draft-list"
    full-width
    remove-card-padding
    :header-label="t('aiOrderEntryDraft.drafts', 2)"
    :loading="requests.loading"
  >
    <template #headerRight>
      <RygenButton
        as="router-link"
        mode="primary"
        :to="{ name: 'aiOrderEntry' }"
        :label="t('aiOrderEntry.aiOrderEntry')"
        icon="pi pi-file-import"
      />
    </template>

    <Section id="ai-order-entry-requests" :header="t('aiOrderEntry.requests', 2)">
      <DataTable
        :value="requests.data"
        :loading="requests.loading"
        data-key="id"
        responsive-layout="scroll"
        removable-sort
        :pt="{ table: { 'aria-label': t('aiOrderEntry.requests', 2) } }"
      >
        <template #empty>
          {{ t('common.noParamYet', { param: t('aiOrderEntry.requests', 2) }) }}
        </template>

        <Column field="submittedAt" :header="t('aiOrderEntry.submitted')" sortable>
          <template #body="{ data }: { data: AiOrderEntryRequest }">
            {{ formatDateTime(data.submittedAt) }}
          </template>
        </Column>

        <Column field="status" :header="t('common.status')" sortable>
          <template #body="{ data }: { data: AiOrderEntryRequest }">
            <RygenChip
              :label="translateEnum(data.status, 'AiOrderEntryRequestStatus')"
              :severity="statusSeverity(data)"
            />
          </template>
        </Column>

        <Column :header="t('common.errorMessages', 1)">
          <template #body="{ data }: { data: AiOrderEntryRequest }">
            {{ data.errorMessage ?? '—' }}
          </template>
        </Column>
      </DataTable>
    </Section>

    <Section
      id="ai-order-entry-drafts"
      :header="t('aiOrderEntryDraft.drafts', 2)"
    >
      <DataTable
        :value="drafts"
        data-key="id"
        responsive-layout="scroll"
        removable-sort
        :pt="{ table: { 'aria-label': t('aiOrderEntryDraft.drafts', 2) } }"
      >
        <template #empty>
          {{ t('common.noParamYet', { param: t('aiOrderEntryDraft.drafts', 2) }) }}
        </template>

        <Column
          field="receivedAt"
          :header="t('aiOrderEntryDraft.received')"
          sortable
        />

        <Column field="billTo" :header="t('aiOrderEntryDraft.billTo')" sortable />

        <Column field="origin" :header="t('orders.origin')" sortable />

        <Column
          field="destination"
          :header="t('orders.destination')"
          sortable
        />

        <Column field="status" :header="t('common.status')" sortable />
      </DataTable>
    </Section>
  </Content>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { DateTime } from 'luxon';
import type { Ref } from 'vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useToast } from '@rygen/rygen-ui/composables';
import { translateEnum } from '@rygen/rygen-ui/i18n';
import { Loadable, Severity } from '@rygen/rygen-ui/model';

import api from '@/api';
import { AiOrderEntryRequest } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';

const router = useRouter();
const { t } = useI18n();
const toast = useToast();

const isAiOrderEntryEnabled = useFlag(
  'corsair-ai-service-order-entry-feat-flag',
);

const requests: Ref<Loadable<AiOrderEntryRequest[]>> = ref({
  loading: false,
  data: [],
});

// TODO: Drafts are empty until CS-11790 gets them back from Corsair AI
const drafts = ref([]);

const formatDateTime = (value?: string) =>
  value ? DateTime.fromISO(value).toFormat('MM/dd/yy, hh:mm a') : '—';

const statusSeverity = (request: AiOrderEntryRequest) => {
  if (request.status === 'FAILED') {
    return Severity.DANGER;
  } else if (request.status === 'COMPLETED') {
    return Severity.SUCCESS;
  } else if (request.status === 'PARTIALLY_COMPLETED') {
    return Severity.WARNING;
  }
  return Severity.INFO;
};

const loadRequests = async () => {
  try {
    requests.value.loading = true;
    requests.value.data = await api.aiOrderEntry.find();
  } catch (e) {
    requests.value.error = e;
    console.error('Error loading AI order entry requests', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('aiOrderEntry.requests', 2),
      }),
      error: e,
    });
  } finally {
    requests.value.loading = false;
  }
};

onMounted(async () => {
  if (!isAiOrderEntryEnabled.value) {
    router.replace({ name: 'orderList' });
    return;
  }

  await loadRequests();
});
</script>
